import { ChevronDown } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from './cn.js';

export type SelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type SelectProps = Omit<
  ComponentPropsWithoutRef<'select'>,
  'children' | 'onChange' | 'value'
> & {
  leadingIcon?: ReactNode;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  value: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, leadingIcon, onValueChange, options, placeholder, value, ...props }, ref) => (
    <div className="relative w-full">
      {leadingIcon ? (
        <span className="pointer-events-none absolute top-1/2 left-3.5 z-10 grid size-4 -translate-y-1/2 place-items-center text-ink-3">
          {leadingIcon}
        </span>
      ) : null}
      <select
        ref={ref}
        className={cn(
          'min-h-11 w-full appearance-none rounded-sm border border-border bg-bg py-2.5 pr-10 pl-3.5 text-left text-base text-ink outline-none transition hover:border-border-hi focus:border-accent focus:ring-3 focus:ring-accent-ring disabled:cursor-not-allowed disabled:opacity-50',
          leadingIcon && 'pl-10',
          value === '' && placeholder && 'text-ink-3',
          className,
        )}
        onChange={(event) => onValueChange(event.currentTarget.value)}
        value={value}
        {...props}
      >
        {placeholder ? (
          <option disabled value="">
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} disabled={option.disabled} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-ink-3"
      />
    </div>
  ),
);
Select.displayName = 'Select';
